/**
 * Typed endpoint functions for `/api/friends/*` (docs/APP_PLAN.md §8.1,
 * §12 M4; APP_SPEC §14).
 */
import type { HttpClient } from './http-client.js';
import { buildQueryString } from './query.js';
import type { Friend, FriendRequestEntry, PaginationParams } from './types.js';

interface SendFriendRequestResult {
  /** True when a pre-existing reciprocal request was auto-accepted into a friendship instead of creating a new request. */
  autoAccepted: boolean;
  request?: FriendRequestEntry;
}

interface FriendRequestListEnvelope {
  requests: FriendRequestEntry[];
}

interface FriendListEnvelope {
  friends: Friend[];
}

export interface FriendsApi {
  /** `POST /api/friends/requests`. */
  send(targetUsername: string): Promise<SendFriendRequestResult>;
  /** `GET /api/friends/requests/incoming`. */
  listIncoming(pagination?: PaginationParams): Promise<FriendRequestEntry[]>;
  /** `GET /api/friends/requests/outgoing`. */
  listOutgoing(pagination?: PaginationParams): Promise<FriendRequestEntry[]>;
  /** `POST /api/friends/requests/:id/accept`. Only the addressee may accept. */
  accept(requestId: string): Promise<void>;
  /** `POST /api/friends/requests/:id/decline`. Only the addressee may decline. */
  decline(requestId: string): Promise<void>;
  /** `POST /api/friends/requests/:id/cancel`. Only the requester may cancel. */
  cancel(requestId: string): Promise<void>;
  /** `GET /api/friends`. */
  list(pagination?: PaginationParams): Promise<Friend[]>;
  /** `DELETE /api/friends/:userId`. */
  remove(userId: string): Promise<void>;
}

export function createFriendsApi(http: HttpClient): FriendsApi {
  return {
    async send(targetUsername) {
      return http.request<SendFriendRequestResult>({
        method: 'POST',
        path: '/friends/requests',
        body: { targetUsername },
      });
    },

    async listIncoming(pagination = {}) {
      const { requests } = await http.request<FriendRequestListEnvelope>({
        method: 'GET',
        path: `/friends/requests/incoming${buildQueryString({ ...pagination })}`,
      });
      return requests;
    },

    async listOutgoing(pagination = {}) {
      const { requests } = await http.request<FriendRequestListEnvelope>({
        method: 'GET',
        path: `/friends/requests/outgoing${buildQueryString({ ...pagination })}`,
      });
      return requests;
    },

    async accept(requestId) {
      await http.requestMaybeEmpty<never>({
        method: 'POST',
        path: `/friends/requests/${encodeURIComponent(requestId)}/accept`,
      });
    },

    async decline(requestId) {
      await http.requestMaybeEmpty<never>({
        method: 'POST',
        path: `/friends/requests/${encodeURIComponent(requestId)}/decline`,
      });
    },

    async cancel(requestId) {
      await http.requestMaybeEmpty<never>({
        method: 'POST',
        path: `/friends/requests/${encodeURIComponent(requestId)}/cancel`,
      });
    },

    async list(pagination = {}) {
      const { friends } = await http.request<FriendListEnvelope>({
        method: 'GET',
        path: `/friends${buildQueryString({ ...pagination })}`,
      });
      return friends;
    },

    async remove(userId) {
      await http.requestMaybeEmpty<never>({
        method: 'DELETE',
        path: `/friends/${encodeURIComponent(userId)}`,
      });
    },
  };
}
